import { supabase } from '../lib/supabaseClient';
import type {
    Scenario,
    ScenarioWithResults,
    CreateScenarioInput,
    UpdateScenarioInput,
    SimulationResultPayload,
} from '../models/simulation';
import { normalizeUnit } from '../utils/units';

/**
 * Database row format for scenarios table
 */
interface ScenarioRow {
    id: string;
    simulation_id: string;
    user_id: string;
    name: string;
    description: string | null;
    distribution_overrides: any;
    selected_node_ids: string[] | null;
    results: any | null;
    graph_data_updated_at: string;
    is_baseline: boolean;
    created_at: string;
    updated_at: string;
}

/**
 * Convert database row to Scenario object
 */
function rowToScenario(row: ScenarioRow): Scenario {
    return {
        id: row.id,
        simulationId: row.simulation_id,
        userId: row.user_id,
        name: row.name,
        description: row.description || undefined,
        distributionOverrides: row.distribution_overrides || {},
        selectedNodeIds: row.selected_node_ids && row.selected_node_ids.length > 0 ? row.selected_node_ids : undefined,
        graphDataUpdatedAt: row.graph_data_updated_at,
        isBaseline: row.is_baseline,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
    };
}

/**
 * Normalizes units in simulation results to ensure consistent formatting.
 */
function normalizeResultUnits(results: SimulationResultPayload): SimulationResultPayload {
    const normalized = structuredClone(results);
    
    // Normalize units in each node result
    Object.values(normalized.results).forEach((result) => {
        if (result.unit) {
            result.unit = normalizeUnit(result.unit);
        }
    });
    
    return normalized;
}

/**
 * Removes per-trial samples before persistence to keep scenario rows compact.
 * Scenario compare views use summary statistics, so raw sample arrays are unnecessary.
 */
function compactScenarioResultsForStorage(results: SimulationResultPayload): SimulationResultPayload {
    const compacted = structuredClone(results);

    Object.values(compacted.results).forEach((result) => {
        if ('samples' in result) {
            delete result.samples;
        }
    });

    return compacted;
}

/**
 * Convert database row to ScenarioWithResults object
 */
function rowToScenarioWithResults(row: ScenarioRow): ScenarioWithResults {
    const scenario = rowToScenario(row);
    const results = row.results as SimulationResultPayload | null | undefined;
    
    // Normalize units when loading from database
    const normalizedResults = results ? normalizeResultUnits(results) : undefined;
    
    return {
        ...scenario,
        results: normalizedResults,
    };
}

/**
 * Load all scenarios for a simulation (without full results)
 */
export async function loadScenarios(simulationId: string): Promise<Scenario[]> {
    const { data, error } = await supabase
        .from('scenarios')
        .select('id, simulation_id, user_id, name, description, distribution_overrides, selected_node_ids, graph_data_updated_at, is_baseline, created_at, updated_at')
        .eq('simulation_id', simulationId)
        .order('created_at', { ascending: true });

    if (error) {
        throw new Error(`Failed to load scenarios: ${error.message}`);
    }

    return (data || []).map(rowToScenario);
}

/**
 * Load a specific scenario with full results
 */
export async function loadScenarioWithResults(scenarioId: string): Promise<ScenarioWithResults | null> {
    const { data, error } = await supabase
        .from('scenarios')
        .select('*')
        .eq('id', scenarioId)
        .single();

    if (error) {
        if (error.code === 'PGRST116') {
            return null; // Not found
        }
        throw new Error(`Failed to load scenario: ${error.message}`);
    }

    return rowToScenarioWithResults(data);
}

/**
 * Load multiple scenarios with full results
 */
export async function loadScenariosWithResults(scenarioIds: string[]): Promise<ScenarioWithResults[]> {
    if (scenarioIds.length === 0) {
        return [];
    }

    const { data, error } = await supabase
        .from('scenarios')
        .select('*')
        .in('id', scenarioIds);

    if (error) {
        throw new Error(`Failed to load scenarios: ${error.message}`);
    }

    return (data || []).map(rowToScenarioWithResults);
}

/**
 * Create a new scenario
 */
export async function createScenario(input: CreateScenarioInput): Promise<Scenario> {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
        throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
        .from('scenarios' as any)
        .insert({
            simulation_id: input.simulationId,
            user_id: user.id,
            name: input.name,
            description: input.description || null,
            distribution_overrides: input.distributionOverrides || {},
            selected_node_ids: input.selectedNodeIds && input.selectedNodeIds.length > 0 ? input.selectedNodeIds : [],
            graph_data_updated_at: input.graphDataUpdatedAt,
            is_baseline: input.isBaseline || false,
        } as any)
        .select()
        .single();

    if (error) {
        if (error.code === '23505') {
            const { data: existingScenario } = await supabase
                .from('scenarios')
                .select('id, simulation_id, user_id, name, description, distribution_overrides, selected_node_ids, graph_data_updated_at, is_baseline, created_at, updated_at')
                .eq('simulation_id', input.simulationId)
                .eq('name', input.name)
                .maybeSingle();

            if (existingScenario) {
                return rowToScenario(existingScenario as ScenarioRow);
            }
        }

        throw new Error(`Failed to create scenario: ${error.message}`);
    }

    return rowToScenario(data);
}

/**
 * Update an existing scenario
 */
export async function updateScenario(scenarioId: string, input: UpdateScenarioInput): Promise<Scenario> {
    const updateData: any = {};
    
    if (input.name !== undefined) updateData.name = input.name;
    if (input.description !== undefined) updateData.description = input.description || null;
    if (input.distributionOverrides !== undefined) updateData.distribution_overrides = input.distributionOverrides;
    if (input.selectedNodeIds !== undefined) updateData.selected_node_ids = input.selectedNodeIds && input.selectedNodeIds.length > 0 ? input.selectedNodeIds : [];
    if (input.graphDataUpdatedAt !== undefined) updateData.graph_data_updated_at = input.graphDataUpdatedAt;
    if (input.isBaseline !== undefined) updateData.is_baseline = input.isBaseline;

    const { data, error } = await (supabase
        .from('scenarios' as any) as any)
        .update(updateData)
        .eq('id', scenarioId)
        .select()
        .single();

    if (error) {
        throw new Error(`Failed to update scenario: ${error.message}`);
    }

    return rowToScenario(data);
}

/**
 * Update scenario results
 */
export async function updateScenarioResults(
    scenarioId: string,
    results: SimulationResultPayload
): Promise<void> {
    // Normalize units before saving to database
    const normalizedResults = normalizeResultUnits(results);
    const compactResults = compactScenarioResultsForStorage(normalizedResults);

    const { error } = await (supabase
        .from('scenarios' as any) as any)
        .update({ results: compactResults })
        .eq('id', scenarioId);

    if (error) {
        throw new Error(`Failed to update scenario results: ${error.message}`);
    }
}

/**
 * Delete a scenario
 */
export async function deleteScenario(scenarioId: string): Promise<void> {
    const { error } = await supabase
        .from('scenarios')
        .delete()
        .eq('id', scenarioId);

    if (error) {
        throw new Error(`Failed to delete scenario: ${error.message}`);
    }
}

/**
 * Delete all scenarios for a simulation
 */
export async function deleteScenariosForSimulation(simulationId: string): Promise<void> {
    const { error } = await supabase
        .from('scenarios')
        .delete()
        .eq('simulation_id', simulationId);

    if (error) {
        throw new Error(`Failed to delete scenarios: ${error.message}`);
    }
}

const generateUniqueScenarioName = (baseName: string, existingNames: Set<string>): string => {
    if (!existingNames.has(baseName)) {
        return baseName;
    }

    let index = 2;
    let candidate = `${baseName} (${index})`;
    while (existingNames.has(candidate)) {
        index += 1;
        candidate = `${baseName} (${index})`;
    }

    return candidate;
};

/**
 * Load all scenarios for a simulation including their results
 */
export async function loadSimulationScenariosWithResults(simulationId: string): Promise<ScenarioWithResults[]> {
    const { data, error } = await supabase
        .from('scenarios')
        .select('*')
        .eq('simulation_id', simulationId)
        .order('created_at', { ascending: true });

    if (error) {
        throw new Error(`Failed to load scenarios: ${error.message}`);
    }

    return (data || []).map(rowToScenarioWithResults);
}

/**
 * Duplicate scenarios (with results) from one simulation to another
 */
export async function duplicateScenarios(
    sourceSimulationId: string,
    targetSimulationId: string,
    targetGraphDataUpdatedAt: string
): Promise<void> {
    const scenarios = await loadSimulationScenariosWithResults(sourceSimulationId);
    if (scenarios.length === 0) {
        return;
    }

    const existingTargetScenarios = await loadScenarios(targetSimulationId);
    const existingNames = new Set(existingTargetScenarios.map((scenario) => scenario.name));

    const {
        data: { user },
        error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
        throw new Error('User not authenticated');
    }

    const rows = scenarios.map((scenario) => {
        const uniqueName = generateUniqueScenarioName(scenario.name, existingNames);
        existingNames.add(uniqueName);

        return {
            simulation_id: targetSimulationId,
            user_id: user.id,
            name: uniqueName,
            description: scenario.description || null,
            distribution_overrides: scenario.distributionOverrides || {},
            selected_node_ids: scenario.selectedNodeIds && scenario.selectedNodeIds.length > 0 ? scenario.selectedNodeIds : [],
            results: scenario.results
                ? compactScenarioResultsForStorage(normalizeResultUnits(scenario.results))
                : null,
            graph_data_updated_at: targetGraphDataUpdatedAt,
            is_baseline: scenario.isBaseline,
        };
    });

    const { error } = await supabase.from('scenarios').insert(rows as any);

    if (error) {
        throw new Error(`Failed to duplicate scenarios: ${error.message}`);
    }
}

/**
 * Check if a scenario name is unique within a simulation
 */
export async function isScenarioNameUnique(simulationId: string, name: string, excludeId?: string): Promise<boolean> {
    let query = supabase
        .from('scenarios')
        .select('id')
        .eq('simulation_id', simulationId)
        .eq('name', name);

    if (excludeId) {
        query = query.neq('id', excludeId);
    }

    const { data, error } = await query;

    if (error) {
        throw new Error(`Failed to check scenario name uniqueness: ${error.message}`);
    }

    return !data || data.length === 0;
}
